import { useRef, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Camera, X, Loader2, Image as ImgIcon } from "lucide-react";

export function fileUrl(path) {
  if (!path) return "";
  const token = typeof window !== "undefined" ? localStorage.getItem("orbit_token") : null;
  const q = token ? `?auth=${encodeURIComponent(token)}` : "";
  return `${API}/files/${path}${q}`;
}

export default function PhotoUploader({ value = [], onChange, testid = "photo-uploader", max = 6, compact = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    const remaining = max - value.length;
    const list = Array.from(files).slice(0, remaining);
    if (list.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of list) {
        if (!f.type.startsWith("image/")) { toast.error("Fichier non image ignoré"); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd);
        uploaded.push(data.path);
      }
      onChange([...(value || []), ...uploaded]);
      toast.success(`${uploaded.length} photo(s) ajoutée(s)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = (path) => onChange(value.filter((p) => p !== path));

  if (compact) {
    return (
      <>
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading || value.length >= max}
                data-testid={`${testid}-btn`}
                className="w-8 h-8 grid place-items-center rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-40"
                title={value.length >= max ? `Max ${max}` : "Joindre une photo"}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Camera className="w-4 h-4"/>}
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple hidden
               onChange={(e) => handleFiles(e.target.files)} data-testid={`${testid}-input`}/>
      </>
    );
  }

  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {value.map((path) => (
          <div key={path} className="relative group aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
            <img src={fileUrl(path)} alt="" className="w-full h-full object-cover"/>
            <button type="button" onClick={() => remove(path)}
                    className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-slate-900/70 text-white opacity-0 group-hover:opacity-100 transition-opacity">
              <X className="w-3.5 h-3.5"/>
            </button>
          </div>
        ))}
        {value.length < max && (
          <button type="button" data-testid={`${testid}-btn`}
                  onClick={() => inputRef.current?.click()} disabled={uploading}
                  className="aspect-square rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 grid place-items-center text-slate-500 hover:text-blue-600 transition-colors disabled:opacity-60">
            {uploading ? <Loader2 className="w-5 h-5 animate-spin"/> : (
              <div className="flex flex-col items-center gap-1">
                <Camera className="w-5 h-5"/>
                <span className="text-[10px] font-semibold">Ajouter</span>
              </div>
            )}
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden
             onChange={(e) => handleFiles(e.target.files)} data-testid={`${testid}-input`}/>
      <div className="text-[11px] text-slate-400 mt-2 flex items-center gap-1">
        <ImgIcon className="w-3 h-3"/> Jusqu'à {max} photos — JPG, PNG, WEBP
      </div>
    </div>
  );
}

export function PhotoStrip({ paths = [] }) {
  if (!paths?.length) return null;
  return (
    <div className="flex gap-2 mt-3 flex-wrap">
      {paths.map((p) => (
        <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer"
           className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 hover:opacity-80 transition-opacity">
          <img src={fileUrl(p)} alt="" className="w-full h-full object-cover"/>
        </a>
      ))}
    </div>
  );
}
